import axios from "axios";
import {
  doc,
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  addDoc,
  serverTimestamp,
  updateDoc,
  setDoc,
  getDoc,
} from "@firebase/firestore";
import db from "services/firebase";
import { showErrorToast, showSuccessToast, showLoadingToast, updateLoadingToast, dismissLoadingToast } from "app/components/ShowToast";

interface SlackChannel {
  id: string;
  name: string;
  is_channel: boolean;
  created: number;
  creator: string;
}

interface SlackResponse {
  success: boolean;
  channels?: SlackChannel[];
  error?: string;
  channelIssues?: string[];
}

interface SlackIntegrationData {
  team_id: string;
  team_name: string;
  access_token: string;
  scope: string;
  bot_user_id: string;
  last_checked: string;
  status: "active" | "access_issue" | "no_channels";
}

interface Task {
  title: string;
  description: string;
  owner: string;
  due_date: string;
  tenantId: string;
  created_at: string;
}

export const createSlackService = (accessToken: string, teamId: string) => {
  const FIREBASE_FUNCTION_URL = "https://us-central1-slate-134a0.cloudfunctions.net/slackProxy";

  const axiosInstance = axios.create({
    headers: {
      "Content-Type": "application/json",
    },
  });

  const checkSlackChannels = async (): Promise<SlackResponse> => {
    try {
      const response = await axiosInstance.get("/conversations.list", {
        params: {
          types: "public_channel,private_channel",
          limit: 100,
        },
      });

      if (!response.data.ok) {
        return {
          success: false,
          error: response.data.error || "Failed to fetch channels",
        };
      }

      return {
        success: true,
        channels: response.data.channels,
      };
    } catch (error: any) {
      if (error.response?.status === 403 || error.response?.data?.error === "insufficient_scope") {
        return {
          success: false,
          error: "Insufficient permissions for accessing channels",
          channelIssues: [teamId],
        };
      }
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  };

  const saveSlackChannels = async (
    tenantId: string,
    channels: SlackChannel[] | null,
    status: "active" | "access_issue" | "no_channels",
    integrationId: string
  ) => {
    try {
      const channelData: SlackIntegrationData = {
        team_id: teamId,
        team_name: "",
        access_token: accessToken,
        scope: "",
        bot_user_id: "",
        last_checked: new Date().toISOString(),
        status,
      };

      const tenantRef = doc(db, "tenants", tenantId);
      const channelsRef = collection(tenantRef, "slack_channels");
      const batch = writeBatch(db);

      if (status === "active" && channels) {
        for (const channel of channels) {
          const existingChannelQuery = query(
            channelsRef,
            where("id", "==", channel.id),
            where("team_id", "==", teamId)
          );
          const existingChannelSnapshot = await getDocs(existingChannelQuery);

          if (existingChannelSnapshot.empty) {
            batch.set(doc(channelsRef), {
              ...channel,
              source: "Slack",
              created_at: new Date().toISOString(),
              scan_date: channelData.last_checked,
            });
          } else {
            const existingDoc = existingChannelSnapshot.docs[0];
            batch.update(doc(channelsRef, existingDoc.id), {
              scan_date: channelData.last_checked,
              name: channel.name,
            });
          }
        }
      } else if (status === "access_issue") {
        // No channels saved for integrations with access issues
      } else if (status === "no_channels") {
        const existingChannelQuery = query(
          channelsRef,
          where("team_id", "==", teamId),
          where("status", "==", "access_issue")
        );
        const existingChannelSnapshot = await getDocs(existingChannelQuery);
        existingChannelSnapshot.forEach((docSnap) => {
          batch.delete(doc(channelsRef, docSnap.id));
        });
      }

      const integrationRef = doc(db, "tenants", tenantId, "integrations", integrationId);
      batch.update(integrationRef, {
        last_scan: new Date().toISOString(),
      });

      await batch.commit();
      return { success: true };
    } catch (error: any) {
      console.error("Error saving Slack channels:", error);
      return {
        success: false,
        error: error.message || "Unknown error",
      };
    }
  };

  const postMessage = async (
    channelId: string,
    message: string
  ): Promise<SlackResponse> => {
    try {
      const response = await axiosInstance.post("/chat.postMessage", {
        channel: channelId,
        text: message,
      });

      if (!response.data.ok) {
        return {
          success: false,
          error: response.data.error || "Failed to post message",
        };
      }

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Failed to post message",
      };
    }
  };

  const joinChannel = async (channelId: string): Promise<SlackResponse> => {
    try {
      const response = await axiosInstance.post(FIREBASE_FUNCTION_URL, {
        action: "joinChannel",
        accessToken,
        channelId,
      });
 
       if (!response.data.success) {
         return {
           success: false,
           error: response.data.error || "Failed to join channel",
         };
       }

      return { success: true };
    } catch (error: any) {
      console.warn("Error joining channel (may already be a member):", error);
      return {
        success: false,
        error: error.message || "Failed to join channel",
      };
    }
  };

  const sendTaskNotification = async (
    channelId: string,
    task: Task,
    tenantId: string,
    integrationId: string
  ): Promise<SlackResponse> => {
    try {
      const blocks = [
        {
          type: "section",
          text: {
            type: "mrkdwn",
            text: `*New Task Created: ${task.title}*`,
          },
        },
        {
          type: "section",
          fields: [
            {
              type: "mrkdwn",
              text: `*Description:*\n${task.description || "No description provided"}`,
            },
            {
              type: "mrkdwn",
              text: `*Owner:*\n${task.owner}`,
            },
            {
              type: "mrkdwn",
              text: `*Due Date:*\n${task.due_date || "Not set"}`,
            },
            {
              type: "mrkdwn",
              text: `*Created:*\n${new Date(task.created_at).toLocaleDateString()}`,
            },
          ],
        },
        {
          type: "actions",
          elements: [
            {
              type: "button",
              text: {
                type: "plain_text",
                text: "View Task",
              },
              url: 'https://app.getsecureslate.com/dashboard'
            },
          ],
        },
      ];

      const response = await axiosInstance.post(FIREBASE_FUNCTION_URL, {
        action: "postMessage",
        accessToken,
        channelId,
        message: blocks, 
      });

      if (!response.data.success) {
        return {
          success: false,
          error: response.data.error || "Failed to send task notification",
        };
      }

      const tenantRef = doc(db, "tenants", tenantId);
      const notificationsRef = collection(tenantRef, "notifications");
      const batch = writeBatch(db);

      batch.set(doc(notificationsRef), {
        channel_id: channelId,
        task_title: task.title,
        task_owner: task.owner,
        sent_at: serverTimestamp(),
        integration_id: integrationId,
        source: "Slack",
      });

      const integrationRef = doc(db, "tenants", tenantId, "integrations", integrationId);
      batch.update(integrationRef, {
        last_notification: new Date().toISOString(),
      });

      await batch.commit();

      return { success: true };
    } catch (error: any) {
      console.error("Error sending task notification:", error);
      return {
        success: false,
        error: error.message || "Failed to send task notification",
      };
    }
  };

  return {
    checkSlackChannels,
    saveSlackChannels,
    postMessage,
    joinChannel,
    sendTaskNotification,
    axiosInstance, 
  };
};

interface SlackUser {
  id: string;
  name: string;
  real_name: string;
  is_bot: boolean;
  is_admin: boolean;
  is_owner: boolean;
  is_primary_owner: boolean;
  is_restricted: boolean;
  is_ultra_restricted: boolean;
  deleted: boolean;
  has_2fa: boolean;
  tz: string;
  tz_label: string;
  tz_offset: number;
  email: string;
  profile: {
    display_name: string;
    real_name: string;
    email: string;
    image_24: string;
    image_32: string;
    image_48: string;
    image_72: string;
    image_192: string;
    image_512: string;
    status_text: string;
    status_emoji: string;
  };
  team_id: string;
  updated: number;
}

export const fetchAndSaveSlackContent = async (
  accessToken: string,
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void,
  slackData: {
    team: { id: string; name: string; url?: string };
    scope: string;
    bot_user_id: string;
    channels: any[];
    users: SlackUser[];
  }
): Promise<{ success: boolean; integrationId?: string; error?: string }> => {
  try {
    if (!accessToken) {
      return { success: false, error: 'No access token found' };
    }

    if (!currentUser || !currentUser.tenantId) {
      return { success: false, error: 'User authentication required' };
    }
    let integrationId: string | undefined;
    const tenantRef = doc(db, 'tenants', currentUser.tenantId);
    const accessReviewRef = doc(tenantRef, 'access_review', 'slack');
    const notionRef = collection(tenantRef, 'integrations');
    const vendorsRef = collection(tenantRef, 'vendors');
    const integrationSnapshot = await getDocs(
      query(notionRef, where('name', '==', 'Slack'))
    );

    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;

    if (integrationSnapshot.empty) {
      const newData = {
        access_token: accessToken,
        name: 'Slack',
        group: 'Team Communication and Collaboration',
        img_path: '/integrations/Slack.svg',
        status: 'Connected',
        connectedAt: formattedDate,
        tenantId: currentUser?.tenantId,
        team_id: slackData.team.id || null,
        team_name: slackData.team.name || '',
        scope: slackData.scope || '',
        bot_user_id: slackData.bot_user_id || '',
        channels: slackData.channels || [],
        uid: currentUser?.uid,
        createdAt: serverTimestamp(),
      };

      const vendorData = {
        authentication_type: 'OAuth',
        category: 'Team Communication and Collaboration',
        created_at: serverTimestamp(),
        due_diligence_status: 'Incomplete',
        impact_assessment: [],
        logo: [
          {
            name: 'slack_logo.png',
            size: 3395,
            url: '/integrations/Slack.svg',
          },
        ],
        owner: currentUser?.userName || '',
        policy_url: 'https://slack.com/legal/privacy-policy',
        reliance: '',
        risk_level: '',
        tenantId: currentUser?.tenantId,
        terms_url: 'https://slack.com/terms-of-service',
        two_factor: true,
        uid: currentUser?.uid,
        updated_at: serverTimestamp(),
        vendor_name: 'Slack',
        website_url: 'https://slack.com',
      };

      const batch = writeBatch(db);
      const docRef = await addDoc(notionRef, newData);
      integrationId = docRef.id;
      await addDoc(vendorsRef, vendorData);
      await batch.commit();
    }

    const membersMap: { [key: string]: any } = {};
    for (const user of slackData.users || []) {
      if (user.id && !user.is_bot && user.id !== 'USLACKBOT') { 
        membersMap[user.id] = {
          id: user.id,
          name: user.name,
          real_name: user.real_name,
          is_admin: user.is_admin,
          is_owner: user.is_owner,
          is_primary_owner: user.is_primary_owner,
          is_restricted: user.is_restricted,
          is_ultra_restricted: user.is_ultra_restricted,
          deleted: user.deleted,
          mfaEnabled: user.has_2fa,
          tz: user.tz,
          tz_label: user.tz_label,
          tz_offset: user.tz_offset,
          email: user.email,
          profile: {
            display_name: user.profile.display_name,
            real_name: user.profile.real_name,
            image_24: user.profile.image_24,
            image_32: user.profile.image_32,
            email: user.profile.email,
            image_48: user.profile.image_48,
            image_72: user.profile.image_72,
            image_192: user.profile.image_192,
            image_512: user.profile.image_512,
            status_text: user.profile.status_text,
            status_emoji: user.profile.status_emoji,
          },
          team_id: user.team_id,
          updated: user.updated,
          platform: 'Slack',
          tenantId: currentUser.tenantId,
          uid: currentUser.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          status: user.deleted ? 'inactive' : 'active',
        };
      }
    }

    // Save or update access review document
    const slackDoc = await getDoc(accessReviewRef);
    if (slackDoc.exists()) {
      await updateDoc(accessReviewRef, {
        members: {
          ...slackDoc.data().members,
          ...membersMap,
        },
        updatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(accessReviewRef, {
        platform: 'Slack',
        members: membersMap,
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    return {
      success: true,
      integrationId: integrationId,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    showErrorToast({ message: 'Error fetching Slack content' });
    navigate('/integrations', { replace: true });
    return { success: false, error: errorMessage };
  }
};

export const exchangeCodeForSlackToken = async (
  code: string,
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void,
  selectedChannelId?: string
) => {
  let toastId;
  try {
    toastId = showLoadingToast({ message: 'Authenticating with Slack...' });
    const tokenResponse = await fetch(
      `https://us-central1-slate-134a0.cloudfunctions.net/slackOAuthCallback?code=${encodeURIComponent(code)}${
        selectedChannelId ? `&channelId=${encodeURIComponent(selectedChannelId)}` : ''
      }`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          Accept: 'application/json',
        },
      }
    );

    const responseText = await tokenResponse.text();
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      throw new Error('Invalid response format from server');
    }

    if (!data.access_token || typeof data.access_token !== 'string' || data.access_token.trim() === '') {
      throw new Error('No valid access token received from server');
    }

    updateLoadingToast(toastId, {
      message: 'Token received, saving integration and users...',
      type: 'info',
      isLoading: true,
    });

    const result = await fetchAndSaveSlackContent(data.access_token, currentUser, navigate, {
      team: data.team,
      scope: data.scope,
      bot_user_id: data.bot_user_id,
      channels: data.channels,
      users: data.users,
    });

    if (!result.success) {
      throw new Error(result.error || 'Failed to save Slack content');
    }

    dismissLoadingToast(toastId);
    showSuccessToast({ message: 'Successfully authenticated with Slack and saved users' });

    navigate('/integrations', { replace: true });
  } catch (error: any) {
    showErrorToast({ message: 'Slack authentication failed. Try again!' });
    dismissLoadingToast(toastId);
    localStorage.removeItem('slack_access_token');
    navigate('/integrations', { replace: true });
    throw error;
  }
};

export const saveSlackIntegration = async (
  data: {
      access_token: string;
      team: { id: string; name: string; url?: string };
      scope: string;
      bot_user_id: string;
      channels: SlackChannel[];
    },
    currentUser: any,
    navigate: (path: string, options?: { replace: boolean }) => void,
    selectedChannelId?: string 
  ) => {
    try {
      const tenantRef = doc(db, "tenants", currentUser?.tenantId);
      const integrationsRef = collection(tenantRef, "integrations");
      const vendorsRef = collection(tenantRef, "vendors");
      const integrationSnapshot = await getDocs(
        query(integrationsRef, where("name", "==", "Slack"))
      );
  
      const currentDate = new Date();
      const formattedDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
  
      if (integrationSnapshot.empty) {
        const channelsToSave = selectedChannelId
          ? data.channels.filter(channel => channel.id === selectedChannelId)
          : data.channels;
  
        const newData = {
          access_token: data.access_token,
          name: "Slack",
          group: "Team Communication and Collaboration",
          img_path: "/integrations/Slack.svg",
          status: "Connected",
          connectedAt: formattedDate,
          tenantId: currentUser?.tenantId,
          team_id: data.team.id || null,
          team_name: data.team.name || '',
          scope: data.scope || '',
          bot_user_id: data.bot_user_id || '',
          channels: channelsToSave, 
          selectedChannel: selectedChannelId || "",
          uid: currentUser?.uid,
          createdAt: serverTimestamp(),
        };
  
        const vendorData = {
          authentication_type: "OAuth",
          category: "Team Communication and Collaboration",
          created_at: serverTimestamp(),
          due_diligence_status: "Incomplete",
          impact_assessment: [],
          logo: [
            {
              name: "slack_logo.png",
              size: 3395,
              url: "/integrations/Slack.svg",
            },
          ],
          owner: currentUser?.userName || "",
          policy_url: "https://slack.com/legal/privacy-policy",
          reliance: "",
          risk_level: "",
          tenantId: currentUser?.tenantId,
          terms_url: "https://slack.com/terms-of-service",
          two_factor: true,
          uid: currentUser?.uid,
          updated_at: serverTimestamp(),
          vendor_name: "Slack",
          website_url: "https://slack.com",
        };
  
        const batch = writeBatch(db);
        await addDoc(integrationsRef, newData);
        await addDoc(vendorsRef, vendorData);
        await batch.commit();
      }
  
      showSuccessToast({ message: "Successfully connected to Slack" });
      navigate("/integrations", { replace: true });
    } catch (error: any) {
      console.error("Error saving Slack integration:", error);
      showErrorToast({ message: "Error saving Slack integration" });
      navigate("/integrations", { replace: true });
      throw error;
    }
  };

export default createSlackService;